import { useState, useRef } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import { 
  Upload, 
  FileSpreadsheet, 
  FolderOpen, 
  Truck, 
  FileText,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Info
} from "lucide-react";


/**
 * DataImportModal - A reusable component for importing PFs, TOs, BLs, and Metrans rates
 * with three import modes: fresh, update, skip
 */
export const DataImportModal = ({ 
  isOpen, 
  onClose, 
  token, 
  onSuccess,
  dataType = "position-folders" // "position-folders" | "transport-orders" | "bl-documents" | "metrans-rates"
}) => {
  const [importMode, setImportMode] = useState("update");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showConfirmFresh, setShowConfirmFresh] = useState(false);
  const fileInputRef = useRef(null);

  const dataTypeConfig = {
    "position-folders": {
      title: "Position Folders (PFs)",
      icon: FolderOpen,
      endpoint: "/api/import/position-folders",
      description: "Import Position Folders from CSV or Excel file",
      columns: "pf_number, customer_name, status, description, assignee, shipping_line, service_type, containers, mbl_number, origin_port, destination_port, eta, remarks",
      acceptedFiles: ".csv,.xlsx,.xls"
    },
    "transport-orders": {
      title: "Transport Orders (TOs)",
      icon: Truck,
      endpoint: "/api/import/transport-orders",
      description: "Import Transport Orders from CSV or Excel file",
      columns: "order_number, status, contact_person, contact_email, loading_place, unloading_place, date_loading, date_unloading, price, currency, remarks, trucking_company_name",
      acceptedFiles: ".csv,.xlsx,.xls"
    },
    "bl-documents": {
      title: "Bill of Lading (BLs)",
      icon: FileText,
      endpoint: "/api/import/bl-documents",
      description: "Import Bill of Lading documents from CSV or Excel file",
      columns: "bl_number, shipper_exporter, consignee, notify_party, port_of_loading, port_of_discharge, vessel_voyage, bl_type (MBL/HBL), pf_number, type_of_move",
      acceptedFiles: ".csv,.xlsx,.xls"
    },
    "metrans-rates": {
      title: "Metrans Rail Rates",
      icon: Truck,
      endpoint: "/api/import/metrans-rates",
      description: "Import Metrans rail rates from JSON (exported backup) or CSV file",
      columns: "country, destination_city, postal_code, price_20, price_40, price_40hc, direction (import/export)",
      acceptedFiles: ".json,.csv,.xlsx,.xls"
    }
  };

  const config = dataTypeConfig[dataType];
  const IconComponent = config.icon;

  const importModes = [
    {
      value: "fresh",
      label: "First Import (Replace All)",
      description: "Delete ALL existing data and import fresh from file",
      icon: AlertTriangle,
      color: "text-destructive"
    },
    {
      value: "update",
      label: "Update Import",
      description: "Update existing records, add new ones",
      icon: CheckCircle2,
      color: "text-success"
    },
    {
      value: "skip",
      label: "Add New Only",
      description: "Skip existing records, only add new entries",
      icon: Info,
      color: "text-primary"
    }
  ];

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    // Show confirmation for fresh import
    if (importMode === "fresh" && !showConfirmFresh) {
      setShowConfirmFresh(true);
      return;
    }

    setImporting(true);
    setImportResult(null);
    setShowConfirmFresh(false);

    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await axios.post(
        `${API_URL}${config.endpoint}?import_mode=${importMode}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setImportResult(response.data);
      
      const { imported, updated, skipped, deleted } = response.data;
      let message = `Import complete! `;
      if (deleted > 0) message += `${deleted} deleted, `;
      if (imported > 0) message += `${imported} imported, `;
      if (updated > 0) message += `${updated} updated, `;
      if (skipped > 0) message += `${skipped} skipped`;
      
      toast.success(message);
      
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setImportFile(null);
    setImportResult(null);
    setShowConfirmFresh(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconComponent className="h-5 w-5 text-primary" />
            Import {config.title}
          </DialogTitle>
          <DialogDescription>
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Import Mode Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Import Mode</Label>
            <Select value={importMode} onValueChange={setImportMode}>
              <SelectTrigger className="w-full" data-testid="import-mode-select">
                <SelectValue placeholder="Select import mode" />
              </SelectTrigger>
              <SelectContent>
                {importModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    <div className="flex items-center gap-2">
                      <mode.icon className={`h-4 w-4 ${mode.color}`} />
                      <span>{mode.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Mode description */}
            <p className="text-xs text-muted-foreground">
              {importModes.find(m => m.value === importMode)?.description}
            </p>
            
            {/* Warning for fresh import */}
            {importMode === "fresh" && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-md border border-destructive/30">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-xs text-destructive">
                  <strong>Warning:</strong> This will permanently delete ALL existing {config.title} before importing. 
                  This action cannot be undone!
                </p>
              </div>
            )}
          </div>

          {/* File Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Select File</Label>
            <input
              type="file"
              accept={config.acceptedFiles || ".csv,.xlsx,.xls"}
              ref={fileInputRef}
              onChange={(e) => setImportFile(e.target.files[0])}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full justify-start"
              data-testid="select-import-file-btn"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              {importFile ? importFile.name : `Choose ${dataType === 'metrans-rates' ? 'JSON or CSV' : 'CSV or Excel'} file...`}
            </Button>
          </div>

          {/* Expected Columns Info */}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              View expected columns
            </summary>
            <div className="mt-2 p-2 bg-muted/50 rounded text-muted-foreground">
              {config.columns}
            </div>
          </details>

          {/* Import Results */}
          {importResult && (
            <div className="p-4 bg-muted/30 rounded-lg border">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success" />
                Import Results
              </h4>
              <div className="grid grid-cols-4 gap-2 text-center">
                {importResult.deleted !== undefined && (
                  <div>
                    <p className="text-lg font-bold text-destructive">{importResult.deleted}</p>
                    <p className="text-xs text-muted-foreground">Deleted</p>
                  </div>
                )}
                <div>
                  <p className="text-lg font-bold text-success">{importResult.imported}</p>
                  <p className="text-xs text-muted-foreground">Imported</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-primary">{importResult.updated}</p>
                  <p className="text-xs text-muted-foreground">Updated</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-muted-foreground">{importResult.skipped}</p>
                  <p className="text-xs text-muted-foreground">Skipped</p>
                </div>
              </div>
              
              {importResult.errors?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-destructive font-medium">Errors ({importResult.errors.length}):</p>
                  <div className="max-h-20 overflow-y-auto mt-1">
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <p key={i} className="text-xs text-muted-foreground">
                        Row {err.row}: {err.error}
                      </p>
                    ))}
                    {importResult.errors.length > 5 && (
                      <p className="text-xs text-muted-foreground">
                        ... and {importResult.errors.length - 5} more
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Confirmation for Fresh Import */}
        {showConfirmFresh && (
          <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/50 mb-4">
            <p className="text-sm text-destructive font-medium mb-3">
              Are you sure you want to delete ALL existing {config.title}?
            </p>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleImport}
                data-testid="confirm-fresh-import-btn"
              >
                Yes, Delete & Import
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConfirmFresh(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {importResult ? "Close" : "Cancel"}
          </Button>
          {!importResult && (
            <Button
              onClick={handleImport}
              disabled={importing || !importFile}
              data-testid="import-data-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * DataImportButton - A button that opens the import modal
 */
export const DataImportButton = ({ 
  token, 
  dataType, 
  onSuccess,
  variant = "outline",
  size = "sm",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const icons = {
    "position-folders": FolderOpen,
    "transport-orders": Truck,
    "bl-documents": FileText,
    "metrans-rates": Truck
  };

  const labels = {
    "position-folders": "Import PFs",
    "transport-orders": "Import TOs",
    "bl-documents": "Import BLs",
    "metrans-rates": "Import Metrans"
  };

  const IconComponent = icons[dataType] || Upload;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setIsOpen(true)}
        className={className}
        data-testid={`import-${dataType}-btn`}
      >
        <IconComponent className="h-4 w-4 mr-2" />
        {labels[dataType] || "Import"}
      </Button>
      
      <DataImportModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        token={token}
        dataType={dataType}
        onSuccess={(result) => {
          if (onSuccess) onSuccess(result);
        }}
      />
    </>
  );
};

export default DataImportModal;
